import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { attributesApi, unitsApi, type AttributeView, type UnitView } from "../../api/catalog";
import type { ApiError } from "../../api/client";
import { detailsMap, formatApiError } from "../../lib/apiError";
import { matchesQuery } from "../../lib/foldText";
import { codebookWrite } from "../../lib/codebookFunctions";
import { useUnits } from "../../lib/useUnits";
import { useAuth } from "../../auth/AuthContext";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { AttributeFormModal, dataTypeLabel } from "../../components/AttributeFormModal";
import { UnitFormModal } from "../../components/UnitFormModal";
import { TextPopover } from "../../components/TextPopover";
import Ic from "../../components/icons.jsx";

type Codebook = "attributes" | "units";
type AttributeForm = { kind: "create" } | { kind: "edit"; row: AttributeView };
type UnitForm = { kind: "create" } | { kind: "edit"; row: UnitView };

/**
 * Two codebooks on one screen, a switch between them (Strahinja, 2026-10-09:
 * no new menu item for units). "Atributi" (sprint 5f): the one place where an
 * attribute is defined — name, key, data type, unit, list values; part types
 * only pick from this list. "Jedinice mere" (sprint 5k): the units an
 * attribute is measured in — symbol, name, note; the attribute form picks from
 * it. Each codebook has its own functions; the screen opens for anyone holding
 * any of the six, and a user without the functions of one codebook still sees
 * it (reading is open to every login) but without its buttons.
 */
export default function AdminAttributesPage() {
  const { hasPermission } = useAuth();
  const canWriteAttributes = codebookWrite("attributes").some((p) => hasPermission(p));
  const [codebook, setCodebook] = useState<Codebook>(canWriteAttributes ? "attributes" : "units");
  const switcher = <CodebookSwitch value={codebook} onChange={setCodebook} />;

  return <div>{codebook === "attributes" ? <AttributesCodebook switcher={switcher} /> : <UnitsCodebook switcher={switcher} />}</div>;
}

/** The switch between the two codebooks; sits in the page head of both. */
function CodebookSwitch({ value, onChange }: { value: Codebook; onChange: (next: Codebook) => void }) {
  const { t } = useTranslation();
  const items: { value: Codebook; label: string }[] = [
    { value: "attributes", label: t("attributes.switch.attributes", "Atributi") },
    { value: "units", label: t("attributes.switch.units", "Jedinice mere") },
  ];
  return (
    <div className="seg" role="group" aria-label={t("attributes.switch.label", "Šifarnik")}>
      {items.map((it) => (
        <button key={it.value} type="button" className={it.value === value ? "is-on" : undefined} aria-pressed={it.value === value} onClick={() => onChange(it.value)}>
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Attributes
// ---------------------------------------------------------------------------

function AttributesCodebook({ switcher }: { switcher: ReactNode }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("attributes:create");
  const canEdit = hasPermission("attributes:edit");
  const canDelete = hasPermission("attributes:delete");
  const hasActions = canEdit || canDelete;

  const [items, setItems] = useState<AttributeView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [form, setForm] = useState<AttributeForm | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await attributesApi.list());
    } catch (err) {
      setError(formatApiError(err as ApiError));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () => (items ?? []).filter((a) => matchesQuery(`${a.label} ${a.key} ${a.unit ?? ""} ${a.note ?? ""}`, q)),
    [items, q],
  );

  async function handleDelete(row: AttributeView) {
    const ok = await confirm({
      title: t("attributes.confirmDelete.title", "Brisanje atributa"),
      message: t("attributes.confirmDelete.message", "Obrisati atribut „{{label}}“ ({{key}}) iz šifarnika?", { label: row.label, key: row.key }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await attributesApi.remove(row.id);
      toast.success(t("attributes.toast.deleted", "Atribut obrisan."));
      await load();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "IN_USE") {
        const count = Number(detailsMap(e)?.typeCount ?? row.typeCount);
        toast.error(t("attributes.error.inUse", "Atribut koristi {{count}} tipova — prvo ga uklonite sa tih tipova.", { count }));
      } else {
        toast.error(formatApiError(e));
      }
    }
  }

  async function handleSaved(_saved: AttributeView, created: boolean) {
    setForm(null);
    toast.success(created ? t("attributes.toast.created", "Atribut dodat u šifarnik.") : t("attributes.toast.saved", "Atribut sačuvan."));
    await load();
  }

  const colLabel = t("attributes.col.label", "Naziv");
  const colKey = t("attributes.col.key", "Ključ");
  const colType = t("attributes.col.dataType", "Tip podatka");
  const colUnit = t("attributes.col.unit", "Jedinica");
  const colEnum = t("attributes.col.enumValues", "Vrednosti liste");
  const colNote = t("attributes.col.note", "Napomena");
  const colTypes = t("attributes.col.typeCount", "Tipova");
  const colActions = t("common.actions", "Radnje");
  const dash = <span className="muted">—</span>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t("attributes.title", "Šifarnik atributa")}</h1>
          <div className="sub">{t("attributes.subtitle", "Svi atributi koje tipovi delova i tipovi artikala mogu da vode — Snaga, Obrtaji, Kućište… Ovde se atribut definiše jednom; u tipu se samo bira i označava da li je obavezan.")}</div>
        </div>
        <div className="page-actions">
          {switcher}
          {canCreate && (
            <button type="button" className="btn primary" onClick={() => setForm({ kind: "create" })}>
              <Ic.plus /> {t("attributes.new", "Novi atribut")}
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("attributes.filter.searchPlaceholder", "Pretraga po nazivu ili ključu…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {q !== "" && (
          <button type="button" className="btn sm ghost" onClick={() => setQ("")}>
            {t("action.clear", "Očisti")}
          </button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card">
        {items === null ? (
          !error && <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : visible.length === 0 ? (
          <div className="empty">
            {items.length === 0
              ? t("attributes.empty", "Šifarnik je prazan. Dodajte prvi atribut.")
              : t("attributes.emptyFiltered", "Nijedan atribut ne odgovara pretrazi.")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{colLabel}</th>
                  <th>{colKey}</th>
                  <th>{colType}</th>
                  <th>{colUnit}</th>
                  <th className="hide-tablet">{colEnum}</th>
                  <th className="hide-tablet">{colNote}</th>
                  <th className="num">{colTypes}</th>
                  {hasActions && <th style={{ textAlign: "right" }}>{colActions}</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr key={a.id}>
                    <td data-label={colLabel}><b>{a.label}</b></td>
                    <td className="mono" data-label={colKey}>{a.key}</td>
                    <td data-label={colType}>{dataTypeLabel(t, a.dataType)}</td>
                    <td data-label={colUnit}>{a.unit || dash}</td>
                    <td className="hide-tablet cell-list" data-label={colEnum}>
                      {a.dataType === "ENUM" && a.enumOptions?.length ? a.enumOptions.map((o) => o.value).join(", ") : dash}
                    </td>
                    <td className="hide-tablet cell-note" data-label={colNote}>{a.note ? <TextPopover text={a.note} /> : dash}</td>
                    <td className="num" data-label={colTypes}>{a.typeCount > 0 ? a.typeCount : dash}</td>
                    {hasActions && (
                      <td className="card-actions" style={{ textAlign: "right", whiteSpace: "nowrap" }} data-label={colActions}>
                        {canEdit && (
                          <button type="button" className="btn sm" onClick={() => setForm({ kind: "edit", row: a })} aria-label={t("attributes.editAria", "Izmeni atribut: {{label}}", { label: a.label })}>
                            {t("action.edit", "Izmeni")}
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" className="btn sm" style={{ ...dangerBtn, marginLeft: 6 }} onClick={() => handleDelete(a)} aria-label={t("attributes.deleteAria", "Obriši atribut: {{label}}", { label: a.label })}>
                            {t("action.delete", "Obriši")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {form && (
        <AttributeFormModal
          attribute={form.kind === "edit" ? form.row : null}
          onClose={() => setForm(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Units of measure
// ---------------------------------------------------------------------------

function UnitsCodebook({ switcher }: { switcher: ReactNode }) {
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("units:create");
  const canEdit = hasPermission("units:edit");
  const canDelete = hasPermission("units:delete");
  const hasActions = canEdit || canDelete;
  const { units, loading, error, refresh } = useUnits();
  const [q, setQ] = useState("");
  const [form, setForm] = useState<UnitForm | null>(null);

  const visible = useMemo(() => units.filter((u) => matchesQuery(`${u.symbol} ${u.name ?? ""} ${u.note ?? ""}`, q)), [units, q]);

  async function handleDelete(row: UnitView) {
    const ok = await confirm({
      title: t("units.confirmDelete.title", "Brisanje jedinice mere"),
      message: t("units.confirmDelete.message", "Obrisati jedinicu „{{symbol}}“ iz šifarnika?", { symbol: row.symbol }),
      confirmLabel: t("action.delete", "Obriši"),
      danger: true,
    });
    if (!ok) return;
    try {
      await unitsApi.remove(row.id);
      toast.success(t("units.toast.deleted", "Jedinica obrisana."));
      await refresh();
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "IN_USE") {
        const count = Number(detailsMap(e)?.attributeCount ?? row.attributeCount);
        toast.error(t("units.error.inUse", "U jedinici se meri {{count}} atributa — prvo im promenite jedinicu.", { count }));
      } else {
        toast.error(formatApiError(e));
      }
    }
  }

  async function handleSaved(_saved: UnitView, created: boolean) {
    setForm(null);
    toast.success(created ? t("units.toast.created", "Jedinica dodata u šifarnik.") : t("units.toast.saved", "Jedinica sačuvana."));
    await refresh();
  }

  const colSymbol = t("units.col.symbol", "Oznaka");
  const colName = t("units.col.name", "Naziv");
  const colNote = t("units.col.note", "Napomena");
  const colAttributes = t("units.col.attributeCount", "Atributa");
  const colActions = t("common.actions", "Radnje");
  const dash = <span className="muted">—</span>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t("units.title", "Šifarnik jedinica mere")}</h1>
          <div className="sub">{t("units.subtitle", "Jedinice u kojima se mere atributi — kW, o/min, mm… Na atributu se jedinica bira iz ove liste, ne kuca se.")}</div>
        </div>
        <div className="page-actions">
          {switcher}
          {canCreate && (
            <button type="button" className="btn primary" onClick={() => setForm({ kind: "create" })}>
              <Ic.plus /> {t("units.new", "Nova jedinica")}
            </button>
          )}
        </div>
      </div>

      <div className="filter-bar">
        <div className="search-wrap">
          <Ic.search aria-hidden="true" />
          <input
            className="input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("units.filter.searchPlaceholder", "Pretraga po oznaci ili nazivu…")}
            aria-label={t("action.search", "Pretraga")}
          />
        </div>
        {q !== "" && (
          <button type="button" className="btn sm ghost" onClick={() => setQ("")}>
            {t("action.clear", "Očisti")}
          </button>
        )}
      </div>

      {error && <div style={errorBox} role="alert">{error}</div>}

      <section className="card">
        {loading ? (
          <div className="empty">{t("common.loading", "Učitavanje…")}</div>
        ) : visible.length === 0 ? (
          <div className="empty">
            {units.length === 0
              ? t("units.empty", "Šifarnik je prazan. Dodajte prvu jedinicu.")
              : t("units.emptyFiltered", "Nijedna jedinica ne odgovara pretrazi.")}
          </div>
        ) : (
          <div className="tbl-wrap">
            <table className="tbl list-card">
              <thead>
                <tr>
                  <th>{colSymbol}</th>
                  <th>{colName}</th>
                  <th className="hide-tablet">{colNote}</th>
                  <th className="num">{colAttributes}</th>
                  {hasActions && <th style={{ textAlign: "right" }}>{colActions}</th>}
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr key={u.id}>
                    <td data-label={colSymbol}><b>{u.symbol}</b></td>
                    <td data-label={colName}>{u.name || dash}</td>
                    <td className="cell-note hide-tablet" data-label={colNote}>{u.note ? <TextPopover text={u.note} /> : dash}</td>
                    <td className="num" data-label={colAttributes}>{u.attributeCount > 0 ? u.attributeCount : dash}</td>
                    {hasActions && (
                      <td className="card-actions" style={{ textAlign: "right", whiteSpace: "nowrap" }} data-label={colActions}>
                        {canEdit && (
                          <button type="button" className="btn sm" onClick={() => setForm({ kind: "edit", row: u })} aria-label={t("units.editAria", "Izmeni jedinicu: {{symbol}}", { symbol: u.symbol })}>
                            {t("action.edit", "Izmeni")}
                          </button>
                        )}
                        {canDelete && (
                          <button type="button" className="btn sm" style={{ ...dangerBtn, marginLeft: 6 }} onClick={() => handleDelete(u)} aria-label={t("units.deleteAria", "Obriši jedinicu: {{symbol}}", { symbol: u.symbol })}>
                            {t("action.delete", "Obriši")}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {form && <UnitFormModal unit={form.kind === "edit" ? form.row : null} onClose={() => setForm(null)} onSaved={handleSaved} />}
    </>
  );
}

const errorBox: CSSProperties = { padding: 10, background: "rgba(182,53,58,.08)", color: "var(--err)", borderRadius: 6, fontSize: 13, marginBottom: 12 };
const dangerBtn: CSSProperties = { color: "var(--err)", borderColor: "rgba(182,53,58,.35)" };
